import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { formatCurrency, moneyAmountLabel } from '@/utils/formatCurrency';
import { adsApi } from '@/api/client';
import { AdGroupFields } from '@/components/campaign-create/AdGroupFields';
import { AdvertisedProductsPicker } from '@/components/campaign-create/AdvertisedProductsPicker';
import { BidAdjustmentsFields } from '@/components/campaign-create/BidAdjustmentsFields';
import { BiddingStrategyFields } from '@/components/campaign-create/BiddingStrategyFields';
import { KeywordTargetingPanel } from '@/components/campaign-create/KeywordTargetingPanel';
import { NegativeKeywordTargetingSection } from '@/components/campaign-create/NegativeKeywordTargetingSection';
import { NegativeProductTargetingSection } from '@/components/campaign-create/NegativeProductTargetingSection';
import { PortfolioSelect } from '@/components/campaign-create/PortfolioSelect';
import { ProductTargetingPanel } from '@/components/campaign-create/ProductTargetingPanel';
import {
  campaignBudgetMin,
  defaultAdGroupName,
  defaultCampaignBudgetInput,
  defaultCampaignDateRange,
  extractAdGroupMutationResults,
  extractSpSbCampaignMutateMessages,
  formatCreateCampaignUpstreamError,
  validateCampaignBudget,
} from '@/utils/campaignCreate';
import { buildNegativeTargetClauses } from '@/utils/spAdGroupCreate';
import {
  buildDynamicBidding,
  buildProductAdPayload,
  extractGenericMutationResults,
  validateKeywordBids,
  validateProductTargetBids,
} from '@/utils/spCampaignCreateDraft';
import type {
  SpAddedKeyword,
  SpAddedNegativeBrand,
  SpAddedNegativeKeyword,
  SpAddedNegativeProduct,
  SpAddedProductTarget,
  SpAdvertisedProduct,
  SpBiddingStrategy,
  SpManualTargetingKind,
  SpTargetingType,
} from '@/types/spCampaignCreate';

export function CreateSpCampaignForm({
  profileId,
  onSuccess,
  onCancel,
}: {
  profileId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [targetingType, setTargetingType] = useState<SpTargetingType>('MANUAL');
  const [manualKind, setManualKind] = useState<SpManualTargetingKind>('KEYWORD');
  const [campaignState, setCampaignState] = useState<'ENABLED' | 'PAUSED'>('ENABLED');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dailyBudget, setDailyBudget] = useState(() => defaultCampaignBudgetInput('SP'));
  const [portfolioId, setPortfolioId] = useState('');
  const [biddingStrategy, setBiddingStrategy] = useState<SpBiddingStrategy>('LEGACY_FOR_SALES');
  const [topOfSearch, setTopOfSearch] = useState('0');
  const [restOfSearch, setRestOfSearch] = useState('0');
  const [productPages, setProductPages] = useState('0');
  const [adGroupName, setAdGroupName] = useState('');
  const [adGroupNameTouched, setAdGroupNameTouched] = useState(false);
  const [defaultBid, setDefaultBid] = useState('1');
  const [advertisedProducts, setAdvertisedProducts] = useState<SpAdvertisedProduct[]>([]);
  const [keywords, setKeywords] = useState<SpAddedKeyword[]>([]);
  const [productTargets, setProductTargets] = useState<SpAddedProductTarget[]>([]);
  const [negativeKeywords, setNegativeKeywords] = useState<SpAddedNegativeKeyword[]>([]);
  const [negativeProducts, setNegativeProducts] = useState<SpAddedNegativeProduct[]>([]);
  const [negativeBrands, setNegativeBrands] = useState<SpAddedNegativeBrand[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const budgetMin = campaignBudgetMin('SP');
  const qc = useQueryClient();

  useEffect(() => {
    const { startDate: s, endDate: e } = defaultCampaignDateRange();
    setName('');
    setTargetingType('MANUAL');
    setManualKind('KEYWORD');
    setCampaignState('ENABLED');
    setStartDate(s);
    setEndDate(e);
    setDailyBudget(defaultCampaignBudgetInput('SP'));
    setPortfolioId('');
    setBiddingStrategy('LEGACY_FOR_SALES');
    setTopOfSearch('0');
    setRestOfSearch('0');
    setProductPages('0');
    setAdGroupName('');
    setAdGroupNameTouched(false);
    setDefaultBid('1');
    setAdvertisedProducts([]);
    setKeywords([]);
    setProductTargets([]);
    setNegativeKeywords([]);
    setNegativeProducts([]);
    setNegativeBrands([]);
    setSubmitError(null);
  }, []);

  useEffect(() => {
    if (!adGroupNameTouched) {
      setAdGroupName(defaultAdGroupName(name));
    }
  }, [name, adGroupNameTouched]);

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
    await qc.invalidateQueries({ queryKey: ['sp-ad-groups', profileId] });
    await qc.invalidateQueries({ queryKey: ['sp-keywords', profileId] });
    await qc.invalidateQueries({ queryKey: ['sp-product-ads', profileId] });
    await qc.invalidateQueries({ queryKey: ['sp-targets', profileId] });
  };

  const failAfterPartial = async (message: string) => {
    setSubmitError(message);
    await invalidate();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setSubmitError('Campaign name is required.');
      return;
    }
    const budgetNum = parseFloat(dailyBudget);
    const budgetError = validateCampaignBudget(budgetNum, 'SP');
    if (budgetError) {
      setSubmitError(budgetError);
      return;
    }
    if (!startDate || !endDate) {
      setSubmitError('Start and end dates are required.');
      return;
    }
    if (endDate < startDate) {
      setSubmitError('End date must be on or after start date.');
      return;
    }
    const trimmedAdGroupName = adGroupName.trim();
    if (!trimmedAdGroupName) {
      setSubmitError('Ad group name is required.');
      return;
    }
    const defaultBidNum = parseFloat(defaultBid);
    if (Number.isNaN(defaultBidNum) || defaultBidNum <= 0) {
      setSubmitError('Default bid must be greater than 0.');
      return;
    }
    if (advertisedProducts.length === 0) {
      setSubmitError('Add at least one product to advertise in this ad group.');
      return;
    }
    if (targetingType === 'MANUAL' && manualKind === 'KEYWORD') {
      const keywordError = validateKeywordBids(keywords);
      if (keywordError) {
        setSubmitError(keywordError);
        return;
      }
    }
    if (targetingType === 'MANUAL' && manualKind === 'PRODUCT') {
      const targetError = validateProductTargetBids(productTargets);
      if (targetError) {
        setSubmitError(targetError);
        return;
      }
    }

    const campaignPayload: Record<string, unknown> = {
      endDate,
      name: trimmed,
      targetingType,
      state: campaignState,
      dynamicBidding: buildDynamicBidding(biddingStrategy, {
        topOfSearch,
        restOfSearch,
        productPages,
      }),
      startDate,
      budget: {
        budgetType: 'DAILY',
        budget: budgetNum,
      },
    };
    if (portfolioId) campaignPayload.portfolioId = portfolioId;

    setSubmitting(true);
    try {
      const res = await adsApi.createSpCampaigns(profileId, { campaigns: [campaignPayload] });
      if (!res.success) {
        setSubmitError(res.message || 'Failed to create campaign');
        return;
      }
      const { errors, successIds } = extractSpSbCampaignMutateMessages(res.data);
      if (errors.length > 0) {
        setSubmitError(errors.join(' '));
        return;
      }
      const campaignId = successIds[0];
      if (!campaignId) {
        setSubmitError('Campaign was created but no campaign ID was returned.');
        return;
      }

      const agRes = await adsApi.createSpAdGroups(profileId, {
        adGroups: [
          {
            campaignId,
            name: trimmedAdGroupName,
            defaultBid: defaultBidNum,
            state: 'ENABLED',
          },
        ],
      });
      if (!agRes.success) {
        await failAfterPartial(`Campaign created, but ad group failed: ${agRes.message || 'Unknown error'}`);
        return;
      }
      const agParsed = extractAdGroupMutationResults(agRes.data);
      if (agParsed.errors.length > 0) {
        await failAfterPartial(`Campaign created, but ad group failed: ${agParsed.errors.join(' ')}`);
        return;
      }
      const adGroupId = agParsed.successIds[0];
      if (!adGroupId) {
        await failAfterPartial('Campaign created, but ad group ID was not returned.');
        return;
      }

      const adsRes = await adsApi.createSpProductAds(profileId, {
        productAds: advertisedProducts.map((product) =>
          buildProductAdPayload(campaignId, adGroupId, product)
        ),
      });
      if (!adsRes.success) {
        await failAfterPartial(
          `Campaign and ad group created, but product ads failed: ${adsRes.message || 'Unknown error'}`
        );
        return;
      }
      const adsParsed = extractGenericMutationResults(adsRes.data, 'adId');
      if (adsParsed.errors.length > 0) {
        await failAfterPartial(
          `Campaign and ad group created, but product ads failed: ${adsParsed.errors.join(' ')}`
        );
        return;
      }

      if (targetingType === 'MANUAL' && manualKind === 'KEYWORD' && keywords.length > 0) {
        const kwRes = await adsApi.createSpKeywords(profileId, {
          keywords: keywords.map((row) => ({
            campaignId,
            adGroupId,
            keywordText: row.keywordText,
            matchType: row.matchType,
            bid: parseFloat(row.bid),
            state: 'ENABLED',
          })),
        });
        if (!kwRes.success) {
          await failAfterPartial(
            `Campaign created, but keywords failed: ${kwRes.message || 'Unknown error'}`
          );
          return;
        }
        const kwParsed = extractGenericMutationResults(kwRes.data, 'keywordId');
        if (kwParsed.errors.length > 0) {
          await failAfterPartial(`Campaign created, but keywords failed: ${kwParsed.errors.join(' ')}`);
          return;
        }
      }

      if (targetingType === 'MANUAL' && manualKind === 'PRODUCT' && productTargets.length > 0) {
        const targetRes = await adsApi.createSpTargets(profileId, {
          targetingClauses: productTargets.map((row) => ({
            campaignId,
            adGroupId,
            expressionType: 'MANUAL',
            state: 'ENABLED',
            bid: parseFloat(row.bid),
            expression: [{ type: row.expressionType, value: row.value }],
          })),
        });
        if (!targetRes.success) {
          await failAfterPartial(
            `Campaign created, but product targets failed: ${targetRes.message || 'Unknown error'}`
          );
          return;
        }
        const targetParsed = extractGenericMutationResults(targetRes.data, 'targetId');
        if (targetParsed.errors.length > 0) {
          await failAfterPartial(
            `Campaign created, but product targets failed: ${targetParsed.errors.join(' ')}`
          );
          return;
        }
      }

      if (negativeKeywords.length > 0) {
        const negKwRes = await adsApi.createSpCampaignNegativeKeywords(profileId, {
          negative_keywords: negativeKeywords.map((row) => ({
            campaign_id: campaignId,
            keyword_text: row.keywordText,
            match_type: row.matchType,
            state: 'ENABLED',
          })),
        });
        if (!negKwRes.success) {
          await failAfterPartial(
            `Campaign created, but negative keywords failed: ${negKwRes.message || 'Unknown error'}`
          );
          return;
        }
      }

      const negativeTargets = buildNegativeTargetClauses({
        campaignId,
        products: negativeProducts,
        brands: negativeBrands,
      });
      if (negativeTargets.length > 0) {
        const negProdRes = await adsApi.createSpCampaignNegativeTargets(profileId, {
          negativeTargetingClauses: negativeTargets,
        });
        if (!negProdRes.success) {
          await failAfterPartial(
            `Campaign created, but negative products failed: ${negProdRes.message || 'Unknown error'}`
          );
          return;
        }
      }

      await invalidate();
      onSuccess();
    } catch (err) {
      setSubmitError(formatCreateCampaignUpstreamError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <p className="text-sm text-foreground-muted mb-4">
        Creates a Sponsored Products campaign, ad group, advertised products, and targeting via Amazon Ads APIs.
      </p>
      {submitError && (
        <div className="mb-4 text-sm text-error bg-error-subtle border border-error/20 rounded-xl px-3 py-2">
          {submitError}
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground-muted mb-1">Campaign name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-border rounded-lg px-3 py-2"
            disabled={submitting}
            placeholder="e.g. Spring manual search"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">Targeting</label>
            <select
              value={targetingType}
              onChange={(e) => setTargetingType(e.target.value as SpTargetingType)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            >
              <option value="MANUAL">Manual</option>
              <option value="AUTO">Auto</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">State</label>
            <select
              value={campaignState}
              onChange={(e) => setCampaignState(e.target.value as 'ENABLED' | 'PAUSED')}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            >
              <option value="ENABLED">Enabled</option>
              <option value="PAUSED">Paused</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">End date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-muted mb-1">{moneyAmountLabel('Daily budget')}</label>
          <input
            type="number"
            step="0.01"
            min={budgetMin ?? undefined}
            value={dailyBudget}
            onChange={(e) => setDailyBudget(e.target.value)}
            className="w-full border border-border rounded-lg px-3 py-2"
            disabled={submitting}
          />
          <p className="text-xs text-foreground-subtle mt-1">
            Amount in your ad account currency.
            {budgetMin != null ? ` Minimum ${formatCurrency(budgetMin)}.` : ''}
          </p>
        </div>

        <PortfolioSelect
          profileId={profileId}
          value={portfolioId}
          onChange={setPortfolioId}
          disabled={submitting}
        />

        <BiddingStrategyFields
          value={biddingStrategy}
          onChange={setBiddingStrategy}
          disabled={submitting}
        />

        <BidAdjustmentsFields
          topOfSearch={topOfSearch}
          restOfSearch={restOfSearch}
          productPages={productPages}
          onTopOfSearchChange={setTopOfSearch}
          onRestOfSearchChange={setRestOfSearch}
          onProductPagesChange={setProductPages}
          defaultBid={defaultBid}
          disabled={submitting}
        />

        <AdGroupFields
          adGroupName={adGroupName}
          onAdGroupNameChange={(value) => {
            setAdGroupNameTouched(true);
            setAdGroupName(value);
          }}
          defaultBid={defaultBid}
          onDefaultBidChange={setDefaultBid}
          showDefaultBid
          disabled={submitting}
        />

        <AdvertisedProductsPicker
          profileId={profileId}
          products={advertisedProducts}
          onChange={setAdvertisedProducts}
          disabled={submitting}
        />

        {targetingType === 'MANUAL' && (
          <div className="space-y-3">
            <div className="flex gap-2 text-sm">
              {(['KEYWORD', 'PRODUCT'] as const).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  className={`px-3 py-1.5 rounded-lg border ${
                    manualKind === kind ? 'border-brand text-brand bg-brand/5' : 'border-border'
                  }`}
                  onClick={() => setManualKind(kind)}
                  disabled={submitting}
                >
                  {kind === 'KEYWORD' ? 'Keyword targeting' : 'Product targeting'}
                </button>
              ))}
            </div>
            {manualKind === 'KEYWORD' ? (
              <KeywordTargetingPanel
                profileId={profileId}
                advertisedProducts={advertisedProducts}
                biddingStrategy={biddingStrategy}
                defaultBid={defaultBid}
                added={keywords}
                onChange={setKeywords}
                disabled={submitting}
              />
            ) : (
              <ProductTargetingPanel
                profileId={profileId}
                advertisedProducts={advertisedProducts}
                defaultBid={defaultBid}
                added={productTargets}
                onChange={setProductTargets}
                disabled={submitting}
              />
            )}
          </div>
        )}

        <NegativeKeywordTargetingSection
          negativeKeywords={negativeKeywords}
          onChange={setNegativeKeywords}
          disabled={submitting}
        />
        <NegativeProductTargetingSection
          negativeProducts={negativeProducts}
          onProductsChange={setNegativeProducts}
          negativeBrands={negativeBrands}
          onBrandsChange={setNegativeBrands}
          disabled={submitting}
        />

        <FormActions submitting={submitting} onCancel={onCancel} />
      </form>
    </>
  );
}

export function FormActions({
  submitting,
  onCancel,
}: {
  submitting: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="flex gap-2 pt-2">
      <button
        type="button"
        onClick={onCancel}
        className="flex-1 border border-border rounded-lg py-2 text-sm"
        disabled={submitting}
      >
        Cancel
      </button>
      <button
        type="submit"
        className="flex-1 bg-brand text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50"
        disabled={submitting}
      >
        {submitting ? 'Creating…' : 'Create campaign'}
      </button>
    </div>
  );
}
